import * as Api from '@ViewModels';
import { css, StyleDeclarationValue } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from 'sonner';
import { useErrorMessages, useUserSession } from '../../../../../models/hooks/appStateHooks';
import { useEventLogging } from '../../../../../models/Logging';
import { useGetExternalIntegrationsQuery, useGetGoogleBusinessMetadata } from '../../../../../queries';
import {
	AccountOperationsViewModel,
	AdminPermission,
	AdminUserSessionContext,
} from '../../../../../viewmodels/AdminViewModels';
import ams360Image from '../../../../../web/assets/ams360.png';
import googleBusinessProfileLogo from '../../../../../web/assets/google_business_profile_logo.png';
import hawkSoftLogo from '../../../../../web/assets/hawksoft_logo.png';
import hubspotLogo from '../../../../../web/assets/hubspot_logo.jpg';
import clioLogo from '../../../../../web/assets/logo_clio.png';
import donorPerfectLogo from '../../../../../web/assets/logo_donorperfect.png';
import epicLogo from '../../../../../web/assets/logo_epic.svg';
import ezlynxLogo from '../../../../../web/assets/logo_ezlynx.png';
import nasasoftLogo from '../../../../../web/assets/logo_nasasoft.png';
import qqLogo from '../../../../../web/assets/logo_qq_catalyst.png';
import redtailLogo from '../../../../../web/assets/logo_redtail.png';
import xanatekLogo from '../../../../../web/assets/logo_xanatek.jpg';
import { Button } from '../../../../../web/components-v2/Button';
import { ConfirmationDialog, IConfirmationDialogOption } from '../../../../../web/components/ConfirmationDialog';
import { IntegrationCard } from '../../../../../web/components/integrations/IntegrationCard';
import { WarningIcon } from '../../../../../web/components/svgs/icons/WarningIcon';
import { GoogleBusinessSetupModal } from '../../../../components/integrations/GoogleBusinessSetupModal';
import { HawksoftSetupModal } from '../../../../components/integrations/hawkSoft/HawksoftSetup';
import { AdminAccountIntegrationsViewModel } from '../../../../viewModels';
import { styleSheet } from './styles';

export const INTEGRATION_DISPLAY_NAMES: Record<Api.ConfigurableIntegrationType, string> = {
	[Api.ConfigurableIntegrationType.Ams360]: 'AMS360',
	[Api.ConfigurableIntegrationType.Clio]: 'Clio',
	[Api.ConfigurableIntegrationType.DonorPerfect]: 'DonorPerfect',
	[Api.ConfigurableIntegrationType.Eclipse]: 'Eclipse',
	[Api.ConfigurableIntegrationType.Epic]: 'Epic',
	[Api.ConfigurableIntegrationType.EzLynx]: 'EZLynx',
	[Api.ConfigurableIntegrationType.GeneralCsv]: 'General CSV',
	[Api.ConfigurableIntegrationType.GoogleBusinessProfile]: 'Google Business',
	[Api.ConfigurableIntegrationType.HawkSoft]: 'HawkSoft',
	[Api.ConfigurableIntegrationType.HubSpot]: 'HubSpot',
	[Api.ConfigurableIntegrationType.PublicAPI]: 'API Keys',
	[Api.ConfigurableIntegrationType.QQ]: 'QQ Catalyst',
	[Api.ConfigurableIntegrationType.Redtail]: 'Redtail',
	[Api.ConfigurableIntegrationType.Xanatek]: 'Xanatek',
};

const accountCleanUpModalOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isDestructive: true,
		representedObject: true,
		title: 'Delete',
	},
	{
		isCancel: true,
		representedObject: false,
		title: 'Cancel',
	},
];

const needAccountResetModalOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCancel: true,
		representedObject: false,
		title: 'OK',
	},
];

const confirmOptions: IConfirmationDialogOption<boolean>[] = [
	{
		isCancel: true,
		representedObject: true,
		title: 'Cancel',
	},
	{
		isCancel: false,
		representedObject: false,
		title: 'Enable DonorPerfect',
	},
];

interface IProps {
	accountVM?: AccountOperationsViewModel;
	adminUserSession?: AdminUserSessionContext;
	className?: string;
	disableHawksoft?(event: React.MouseEvent<HTMLElement>): void;
	enableHawksoft?(event: React.MouseEvent<HTMLElement>): void;
	onHawksoftSetupModalRequestClose?(agencyId?: string, cancel?: boolean): void;
	searchQuery?: string;
	showHawksoftSetup?: boolean;
	styles?: StyleDeclarationValue[];
}

const _AdminIntegrations = ({
	accountVM,
	adminUserSession,
	className,
	onHawksoftSetupModalRequestClose,
	searchQuery,
	showHawksoftSetup,
	styles,
}: IProps) => {
	const errorMessages = useErrorMessages();
	const userSession = useUserSession();
	const history = useHistory();
	const { logApiError } = useEventLogging();
	const [showGoogleBusinessSetup, setShowGoogleBusinessSetup] = useState(false);

	const [account] = useState<AccountOperationsViewModel>(
		new AccountOperationsViewModel(userSession as AdminUserSessionContext, accountVM.account)
	);
	const [accountIntegrationsVm] = useState<AdminAccountIntegrationsViewModel>(
		new AdminAccountIntegrationsViewModel(userSession as AdminUserSessionContext, accountVM.account)
	);

	const impersonationContext = useMemo(() => {
		return accountIntegrationsVm?.account
			? {
					account: { id: accountIntegrationsVm.account.id },
				}
			: null;
	}, [accountIntegrationsVm]);

	const { data: googleBusinessMetadata } = useGetGoogleBusinessMetadata({
		impersonationContext,
	});

	const { data: externalIntegrations } = useGetExternalIntegrationsQuery({
		impersonationContext,
	});

	const isGoogleBusinessEnabled =
		googleBusinessMetadata?.connectionState === Api.GoogleBusinessConnectionState.Connected;

	// Check if V2 integrations are enabled AND configured
	const eclipseV2 = externalIntegrations?.find(i => i.provider === Api.IntegrationProvider.Eclipse);
	const isEclipseV2EnabledAndConfigured = eclipseV2?.enabled && !eclipseV2?.configuration?.needsConfiguration;

	const clioV2 = externalIntegrations?.find(i => i.provider === Api.IntegrationProvider.Clio);
	const isClioV2EnabledAndConfigured = clioV2?.enabled && !clioV2?.configuration?.needsConfiguration;

	useEffect(() => {
		account.load().catch((error: Api.IOperationResultNoValue) => {
			logApiError('GetAccount-Error', error);
			errorMessages.push({
				messages: ['Could not load the account'],
			});
		});

		accountIntegrationsVm.load().catch((error: Api.IOperationResultNoValue) => {
			if (error.systemCode !== 404) {
				logApiError('GetIntegrations-Error', error);
				errorMessages.push({});
			}
		});
	}, [account, accountIntegrationsVm, errorMessages, logApiError]);

	const onRenderManageButtons = (integrationType: Api.ConfigurableIntegrationType, isEnabled: boolean) => {
		return isEnabled ? (
			<div className={css(styleSheet.buttonContainer)}>
				<Button variant='secondary' size='sm' onClick={onManage(integrationType)}>
					Manage
				</Button>
				<Button variant='destructive-secondary' size='sm' onClick={onToggleEnabled(integrationType, false)}>
					Disable
				</Button>
			</div>
		) : (
			<Button variant='secondary' size='sm' onClick={onToggleEnabled(integrationType, true)}>
				Enable
			</Button>
		);
	};

	const onManage = (integrationType: Api.ConfigurableIntegrationType) => () => {
		history.push(`/accounts/${accountIntegrationsVm.account.id}/integration/${integrationType}`);
	};

	const [isAccountCleanUpModalOpen, setIsAccountCleanUpModalOpen] = React.useState(false);
	const [isConfirmingDonorPerfectOpen, setIsConfirmingDonorPerfectOpen] = React.useState(false);
	const [isNeedResetModalOpen, setIsNeedResetModalOpen] = React.useState(false);
	const [accountCleanUpIntegrationType, setAccountCleanUpIntegrationType] =
		React.useState<Api.ConfigurableIntegrationType>(null);
	const [accountCleanUpIntegrationEnabled, setAccountCleanUpIntegrationEnabled] = React.useState(false);

	const onToggleEnabled = (integrationType: Api.ConfigurableIntegrationType, enabled: boolean) => async () => {
		setAccountCleanUpIntegrationType(integrationType);
		setAccountCleanUpIntegrationEnabled(enabled);

		if (enabled === true) {
			const enableAbility = await accountIntegrationsVm.getIntegrationEnableAbility(integrationType);
			if (enableAbility === Api.EnableAbility.CleanUpRequired) {
				const hasResetAccountPermission = !!adminUserSession.hasPermission(AdminPermission.ResetAccount);
				if (hasResetAccountPermission) {
					setIsAccountCleanUpModalOpen(true);
				} else {
					setIsNeedResetModalOpen(true);
				}
			} else if (enableAbility === Api.EnableAbility.CannotEnable) {
				toast.error(`There is already an enabled integration on this account.`);
			} else {
				enableIntegration(integrationType, enabled);
			}
		} else {
			enableIntegration(integrationType, enabled);
		}
	};

	const onAccountCleanUpModalConfirmationDialogRequestClose = (
		result?: IConfirmationDialogOption<boolean>,
		canceled?: boolean
	) => {
		if (!!result?.isDestructive && !canceled) {
			account
				.resetAccountAndIntegration(accountIntegrationsVm?.account)
				?.then(() => {
					enableIntegration(accountCleanUpIntegrationType, accountCleanUpIntegrationEnabled);
				})
				?.catch((error: Api.IOperationResultNoValue) => {
					errorMessages.pushApiError(error);
					logApiError('ResetAccountAndIntegrationError from integration switch', error);
					return;
				});
		}

		setIsAccountCleanUpModalOpen(false);
	};

	const onDonorPerfectConfirmRequestClose = async (
		_result?: IConfirmationDialogOption<boolean>,
		canceled?: boolean
	) => {
		if (!canceled) {
			await onToggleEnabled(Api.ConfigurableIntegrationType.DonorPerfect, true)();
		}

		setIsConfirmingDonorPerfectOpen(false);
	};

	const enableIntegration = (integrationType: Api.ConfigurableIntegrationType, enabled: boolean) => {
		switch (integrationType) {
			case Api.ConfigurableIntegrationType.Ams360:
				accountIntegrationsVm.ams360.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.HawkSoft:
				accountIntegrationsVm.hawksoft.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Redtail:
				accountIntegrationsVm.redtail.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Eclipse:
				accountIntegrationsVm.eclipse.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Clio:
				accountIntegrationsVm.clio.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.EzLynx:
				accountIntegrationsVm.ezlynx.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Xanatek:
				accountIntegrationsVm.xanatek.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.Epic:
				accountIntegrationsVm.epic.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.QQ:
				accountIntegrationsVm.qq.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.HubSpot:
				accountIntegrationsVm.hubspot.configure({ enabled });
				break;
			case Api.ConfigurableIntegrationType.DonorPerfect:
				accountIntegrationsVm.donorPerfect.configure({ enabled });
				break;
			default:
				break;
		}
	};

	const isAms360Enabled = accountIntegrationsVm?.ams360?.enabled;
	const isHawksoftEnabled = accountIntegrationsVm?.hawksoft?.enabled;
	const isHubspotEnabled = accountIntegrationsVm?.hubspot?.enabled;
	const isRedtailEnabled = accountIntegrationsVm?.redtail?.enabled;
	const isEclipseEnabled = accountIntegrationsVm?.eclipse?.enabled;
	const isClioEnabled = accountIntegrationsVm?.clio?.enabled;
	const isEzLynxEnabled = accountIntegrationsVm?.ezlynx?.enabled;
	const isXanatekEnabled = accountIntegrationsVm?.xanatek?.enabled;
	const isEpicEnabled = accountIntegrationsVm?.epic?.enabled;
	const isQQEnabled = accountIntegrationsVm?.qq?.enabled;
	const isDonorPerfectEnabled = accountIntegrationsVm?.donorPerfect?.enabled;
	const accountsAccount = account?.account;
	if (!accountsAccount) {
		return null;
	}

	const trimmedSearch = searchQuery?.trim() ?? '';
	const isSearching = trimmedSearch.length > 0;
	const matchesSearch = (name: string) => !isSearching || name.toLowerCase().includes(trimmedSearch.toLowerCase());

	return (
		<div className={`${css(styleSheet.container, ...(styles || []))} ${className || ''}`}>
			<div className={css(styleSheet.items)}>
				{matchesSearch(INTEGRATION_DISPLAY_NAMES[Api.ConfigurableIntegrationType.HawkSoft]) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isHawksoftEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={hawkSoftLogo} />}
						key='hawksoft'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.HawkSoft, isHawksoftEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{matchesSearch(INTEGRATION_DISPLAY_NAMES[Api.ConfigurableIntegrationType.HubSpot]) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isHubspotEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={hubspotLogo} />}
						key='hubspot'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.HubSpot, isHubspotEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{matchesSearch(INTEGRATION_DISPLAY_NAMES[Api.ConfigurableIntegrationType.Ams360]) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isAms360Enabled}
						icon={<img className={css(styleSheet.itemIcon)} src={ams360Image} />}
						key='ams360'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Ams360, isAms360Enabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{/* V1 Eclipse: Only show if V1 is enabled AND V2 is not (enabled AND configured), or if dev */}
				{matchesSearch(INTEGRATION_DISPLAY_NAMES[Api.ConfigurableIntegrationType.Eclipse]) &&
					(adminUserSession.hasPermission(AdminPermission.OnlyForDevelopers) ||
						(isEclipseEnabled && !isEclipseV2EnabledAndConfigured)) && (
						<IntegrationCard
							canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
							enabled={isEclipseEnabled}
							icon={<img className={css(styleSheet.itemIcon)} src={nasasoftLogo} />}
							key='eclipse'
							onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Eclipse, isEclipseEnabled)}
							styles={[styleSheet.item]}
						/>
					)}
				{matchesSearch(INTEGRATION_DISPLAY_NAMES[Api.ConfigurableIntegrationType.EzLynx]) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isEzLynxEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={ezlynxLogo} />}
						key='ezlynx'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.EzLynx, isEzLynxEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{matchesSearch(INTEGRATION_DISPLAY_NAMES[Api.ConfigurableIntegrationType.Xanatek]) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isXanatekEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={xanatekLogo} />}
						key={Api.ConfigurableIntegrationType.Xanatek}
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Xanatek, isXanatekEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{matchesSearch(INTEGRATION_DISPLAY_NAMES[Api.ConfigurableIntegrationType.Epic]) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isEpicEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={epicLogo} />}
						key='epic'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Epic, isEpicEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{matchesSearch(INTEGRATION_DISPLAY_NAMES[Api.ConfigurableIntegrationType.QQ]) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isQQEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={qqLogo} />}
						key='qq'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.QQ, isQQEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{matchesSearch(INTEGRATION_DISPLAY_NAMES[Api.ConfigurableIntegrationType.Redtail]) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isRedtailEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={redtailLogo} />}
						key='redtail'
						onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Redtail, isRedtailEnabled)}
						styles={[styleSheet.item]}
					/>
				)}
				{/* V1 Clio: Only show if V1 is enabled AND V2 is not (enabled AND configured), or if dev */}
				{matchesSearch(INTEGRATION_DISPLAY_NAMES[Api.ConfigurableIntegrationType.Clio]) &&
					(adminUserSession.hasPermission(AdminPermission.OnlyForDevelopers) ||
						(isClioEnabled && !isClioV2EnabledAndConfigured)) && (
						<IntegrationCard
							canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
							enabled={isClioEnabled}
							icon={<img className={css(styleSheet.itemIcon)} src={clioLogo} />}
							key='clio'
							onRenderCtaButton={() => onRenderManageButtons(Api.ConfigurableIntegrationType.Clio, isClioEnabled)}
							styles={[styleSheet.item]}
						/>
					)}
				{matchesSearch(INTEGRATION_DISPLAY_NAMES[Api.ConfigurableIntegrationType.DonorPerfect]) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isDonorPerfectEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={donorPerfectLogo} />}
						key='donorPerfect'
						onRenderCtaButton={() => {
							if (isDonorPerfectEnabled) {
								return onRenderManageButtons(Api.ConfigurableIntegrationType.DonorPerfect, isDonorPerfectEnabled);
							}
							return (
								<Button variant='secondary' size='sm' onClick={() => setIsConfirmingDonorPerfectOpen(true)}>
									Enable
								</Button>
							);
						}}
						styles={[styleSheet.item]}
					/>
				)}
				{matchesSearch(INTEGRATION_DISPLAY_NAMES[Api.ConfigurableIntegrationType.GoogleBusinessProfile]) && (
					<IntegrationCard
						canManage={adminUserSession.hasPermission(AdminPermission.ModifyAccount)}
						enabled={isGoogleBusinessEnabled}
						icon={<img className={css(styleSheet.itemIcon)} src={googleBusinessProfileLogo} />}
						key='google-business-integration'
						onRenderCtaButton={() => {
							if (!isGoogleBusinessEnabled) {
								return null;
							}
							return (
								<Button variant='secondary' size='sm' onClick={() => setShowGoogleBusinessSetup(true)}>
									Manage
								</Button>
							);
						}}
						styles={[styleSheet.item, styleSheet.googleBusinessItem]}
					/>
				)}
			</div>
			<HawksoftSetupModal
				modalProps={{
					isOpen: showHawksoftSetup,
					onRequestClose: onHawksoftSetupModalRequestClose,
				}}
			/>
			<GoogleBusinessSetupModal
				impersonationContext={impersonationContext}
				isOpen={showGoogleBusinessSetup}
				setIsOpen={setShowGoogleBusinessSetup}
				onRequestClose={() => setShowGoogleBusinessSetup(false)}
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isAccountCleanUpModalOpen,
					onRequestClose: onAccountCleanUpModalConfirmationDialogRequestClose,
				}}
				options={accountCleanUpModalOptions}
				title='There were integrations previously enabled on this account.  To permanently delete all previous data including contacts, companies, tags, households, connection types, text messages, donations, and integration settings, click Delete.  Otherwise, click Cancel and contact Engineering for help.'
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isNeedResetModalOpen,
					onRequestClose: () => setIsNeedResetModalOpen(false),
				}}
				options={needAccountResetModalOptions}
				title='There were integrations previously enabled on this account. Please contact Support or Engineering to request cleanup.'
			/>
			<ConfirmationDialog
				icon={<WarningIcon />}
				modalProps={{
					isOpen: isConfirmingDonorPerfectOpen,
					onRequestClose: onDonorPerfectConfirmRequestClose,
				}}
				options={confirmOptions}
				title='Enabling DonorPerfect will result in all existing donations being archived and users will no longer be able to manually import or edit donation data in Levitate.'
			/>
		</div>
	);
};

export const AdminIntegrations = observer(_AdminIntegrations);
